import { Component, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DfDatePickerModule, DfTooltipModule } from '@design-factory/design-factory';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { filter, finalize } from 'rxjs';
import { HistorySearchCriteria, RuleHistoryService } from '../../service/rule-history.service';
import { map, take } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { selectLoggedInUser } from '../../store/core/selector';
import { LoggedInUser } from '../../service/model';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { convertModuleHistoryToRows, RuleHistoryRow, TimeRange } from '../../util/history-rule-utils';
import { LoadingAlertComponent } from '../loading-alert/loading-alert.component';
import { CopyToClipboardButtonComponent } from '../copy-to-clipboard-button/copy-to-clipboard-button.component';

@Component({
  selector: 'ama-app-rule-history',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    DfTooltipModule,
    DfDatePickerModule,
    AgGridAngular,
    NgbAlert,
    LoadingAlertComponent
  ],
  templateUrl: './rule-history.component.html',
  standalone: true,
  styleUrl: './rule-history.component.scss'
})
export class RuleHistoryComponent {
  ruleForm: FormGroup;

  isLoading = signal<boolean>(false);
  showError = signal<boolean>(false);
  ruleOwner: string | undefined;

  columnDefs: ColDef[] = [
    {
      headerName: $localize`:@@upp.history.rule.grid.header.id:Rule ID`,
      field: 'ruleId',
      minWidth: 200,
      cellRenderer: CopyToClipboardButtonComponent
    },
    {
      headerName: $localize`:@@upp.history.rule.grid.header.name:Rule name`,
      field: 'ruleName'
    },
    {
      headerName: $localize`:@@upp.history.rule.grid.header.action:Action`,
      field: 'action'
    },
    {
      headerName: $localize`:@@upp.history.rule.grid.header.date:Date`,
      field: 'date'
    },
    {
      headerName: $localize`:@@upp.history.rule.grid.header.time:Time`,
      field: 'time'
    },
    {
      headerName: $localize`:@@upp.history.rule.grid.header.user:User`,
      field: 'user'
    }
  ];

  rowData: RuleHistoryRow[] = [];

  defaultColDef: ColDef = {
    flex: 1,
    sortable: true,
    suppressMovable: true,
    resizable: false
  };
  gridOptions = {
    theme: 'legacy' as const,
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 30],
    localeText: {
      pageSizeSelectorLabel: $localize`:@@upp.global.pagination.recordsPerPage:Records per page:`
    },
    unSortIcon: true,
    suppressCellFocus: true,
    domLayout: 'autoHeight' as const,
    scrollbarWidth: 0,
    suppressHorizontalScroll: false,
    suppressColumnMoveAnimation: true
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly http: HttpClient,
    private readonly ruleHistoryService: RuleHistoryService,
    private readonly store: Store
  ) {
    this.ruleForm = this.fb.group(
      {
        user: [''],
        ruleId: [''],
        fromDate: ['', Validators.required],
        toDate: ['', Validators.required]
      },
      { validators: this.dateRangeValidator }
    );

    this.store
      .pipe(select(selectLoggedInUser), filter<LoggedInUser | undefined>(Boolean), take(1))
      .subscribe((user) => {
        this.ruleOwner = user?.selectedPartition ? user.selectedPartition : user?.organization;
      });
  }

  dateRangeValidator(group: FormGroup): ValidationErrors | null {
    const fromDate = group.get('fromDate')?.value;
    const toDate = group.get('toDate')?.value;

    if (!fromDate || !toDate) {
      return null;
    }

    const from = new Date(fromDate.year, fromDate.month - 1, fromDate.day);
    const to = new Date(toDate.year, toDate.month - 1, toDate.day);

    return from <= to ? null : { dateRange: true };
  }

  clearForm() {
    this.ruleForm.reset();
    this.rowData = [];
  }

  submitForm() {
    if (!this.ruleForm.valid) {
      this.ruleForm.markAllAsTouched();
      return;
    }

    const formValue = this.ruleForm.value;
    const timeRange = {
      begin: this.formatDate(formValue.fromDate, false),
      end: this.formatDate(formValue.toDate, true)
    };

    this.isLoading.set(true);
    this.loadAllRulesHistory(formValue.user, timeRange, formValue.ruleId);
  }

  loadAllRulesHistory(userId: string, timeRange: TimeRange, ruleId: string): void {
    const criteria: HistorySearchCriteria = {
      owner: this.ruleOwner,
      userID: userId,
      ruleId: ruleId,
      timeRange
    };

    this.ruleHistoryService
      .getRuleHistory(criteria)
      .pipe(
        map((response) => this.filterHistoryRows(convertModuleHistoryToRows(response), userId, timeRange, ruleId)),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (records) => {
          this.rowData = records;
          this.showError.set(records.length === 0);
        },
        error: () => {
          this.rowData = [];
          this.showError.set(true);
        }
      });
  }

  private filterHistoryRows(
    rows: RuleHistoryRow[],
    userId: string | undefined,
    timeRange: TimeRange,
    ruleId: string | undefined = undefined
  ): RuleHistoryRow[] {
    return rows.filter((row) => {
      const rowDate = new Date(`${row.date}T${row.time}`);
      const beginDate = new Date(timeRange.begin);
      const endDate = timeRange.end ? new Date(timeRange.end) : undefined;

      const isInTimeRange = rowDate >= beginDate && (!endDate || rowDate <= endDate);
      const isUserMatch = !userId || row.user === userId;
      const isRuleIdMatch = !ruleId || row.ruleId === ruleId;

      return isInTimeRange && isUserMatch && isRuleIdMatch;
    });
  }

  private formatDate(date: { year: number; month: number; day: number }, isEndDate: boolean = false): string {
    const time = isEndDate ? '23:59:59Z' : '00:00:00Z';
    return date
      ? `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}T${time}`
      : '';
  }

  closeNotification() {
    this.showError.set(false);
  }
}
